import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { CUSTOM_ORDERS_ENABLED } from './lib/shop';
import { LocaleProvider } from './i18n/LocaleContext';
import { LocaleAutoDetect } from './i18n/LocaleSwitch';
import { translatePath } from './i18n/routes';
import { motion } from 'framer-motion';
import { Toaster } from './components/ui/sonner';
import { CartProvider } from './contexts/CartContext';
import { AuthProvider } from './contexts/AuthContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import ScrollToTop from './components/ScrollToTop';
import SmoothScroll from './components/motion/SmoothScroll';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import CustomCursor from './components/cursor/CustomCursor';
import Home from './pages/Home';
import Shop from './pages/Shop';
import Product from './pages/Product';
import Cart from './pages/Cart';
import About from './pages/About';
import Contact from './pages/Contact';
import Account from './pages/Account';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import LegalPage from './pages/LegalPage';
import NotFound from './pages/NotFound';
import './App.css';
import './styles/AtelierEditorial.css';

const Personalisation = lazy(() => import('./pages/Personalisation'));
const Checkout = lazy(() => import('./pages/Checkout'));
const CheckoutSuccess = lazy(() => import('./pages/CheckoutSuccess'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const Admin = lazy(() => import('./pages/Admin'));

// Pages publiques, déclarées une fois avec leur adresse française ; l'adresse anglaise
// (/en/…) est déduite de i18n/routes.js.
const PAGES = [
  ['/', <Home />],
  ['/boutique', <Shop />],
  ['/produit/:slug', <Product />],
  ['/panier', <Cart />],
  ['/checkout', <Checkout />, 'noFooter'],
  ['/commande-confirmee', <CheckoutSuccess />],
  ['/a-propos', <About />],
  ['/contact', <Contact />],
  ['/connexion', <Login />],
  ['/inscription', <Register />],
  ['/mot-de-passe-oublie', <ForgotPassword />],
  ['/reinitialiser-mot-de-passe', <ResetPassword />],
  ['/compte', <Account />],
  ['/journal', <Blog />],
  ['/journal/:slug', <BlogPost />],
  ['/mentions-legales', <LegalPage page="mentions-legales" />],
  ['/cgv', <LegalPage page="cgv" />],
  ['/confidentialite', <LegalPage page="confidentialite" />],
  ['/livraison', <LegalPage page="livraison" />],
];

function RouteFallback() {
  return <div className="min-h-screen bg-background" aria-busy="true" />;
}

// Capture des utm_* à l'arrivée (premier contact conservé) pour l'attribution au checkout.
function UtmCapture() {
  const { search } = useLocation();
  React.useEffect(() => {
    const params = new URLSearchParams(search);
    const utm = {};
    params.forEach((v, k) => { if (k.startsWith('utm_')) utm[k] = v; });
    try {
      if (Object.keys(utm).length && !sessionStorage.getItem('lpd.utm')) sessionStorage.setItem('lpd.utm', JSON.stringify(utm));
    } catch { }
  }, [search]);
  return null;
}

// Bandeau doux quand Margot met l'atelier en pause (toggle dans l'admin).
function PauseBanner() {
  const [settings, setSettings] = React.useState(null);
  React.useEffect(() => {
    fetch(`${(process.env.REACT_APP_BACKEND_URL || '')}/api/settings/public`)
      .then(r => (r.ok ? r.json() : null))
      .then(setSettings)
      .catch(() => {});
  }, []);
  if (!settings?.shop_paused) return null;
  return (
    <div className="fixed top-0 inset-x-0 z-[60] border-b border-[hsl(var(--foreground)/0.15)] bg-[hsl(var(--brand-butter))] px-4 py-2 text-center font-sans text-sm font-semibold text-foreground">
      {settings.pause_message}
    </div>
  );
}

function Layout({ children, hideFooter = false }) {
  const { pathname } = useLocation();
  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <motion.main
        key={pathname}
        className="flex-1"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.main>
      {!hideFooter && <Footer />}
    </div>
  );
}

function AppCursor() {
  const { pathname } = useLocation();

  return (
    <CustomCursor
      forceEnabled={process.env.NODE_ENV === 'development'}
      disabled={pathname.startsWith('/personalisation') || pathname.startsWith('/sur-mesure')}
    />
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <LocaleProvider>
        <LocaleAutoDetect />
        <ScrollToTop />
        <SmoothScroll />
        <AuthProvider>
          <CartProvider>
            <AppCursor />
            <UtmCapture />
            <PauseBanner />
            <Toaster position="top-right" richColors />
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                {PAGES.flatMap(([frPath, page, layout]) => {
                  const element = layout === 'none' ? page : <Layout hideFooter={layout === 'noFooter'}>{page}</Layout>;
                  const enPath = translatePath(frPath, 'en');
                  return [
                    <Route key={frPath} path={frPath} element={element} />,
                    <Route key={enPath} path={enPath} element={element} />,
                  ];
                })}
                <Route path="/personalisation" element={CUSTOM_ORDERS_ENABLED ? <Layout hideFooter><Personalisation /></Layout> : <Navigate to="/boutique" replace />} />
                <Route path="/sur-mesure" element={CUSTOM_ORDERS_ENABLED ? <Layout hideFooter><Personalisation /></Layout> : <Navigate to="/boutique" replace />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="*" element={<Layout><NotFound /></Layout>} />
              </Routes>
            </Suspense>
          </CartProvider>
        </AuthProvider>
        </LocaleProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
